"use client";

import type { FC, ReactNode } from "react";

import type { ChildrenProp } from "@huub-web/ui";
import { Flex } from "@huub-web/ui";
import { TaskBar } from "@huub-web/ui";
import { ProgressBar } from "@huub-web/ui";

import { useNavigationState } from "../../providers";

import {
  baseContainerStyle,
  contentContainerStyle,
  layoutContainerStyle,
  layoutMaskStyle,
  progressBarStyle,
} from "./AppLayout.css";

export type AppLayoutProps = ChildrenProp & {
  /**
   * Optional leading content rendered above the main content area.
   */
  leading?: ReactNode;
  /**
   * The navigation element to render in the layout.
   */
  navigation: ReactNode;
  /**
   * The task bar element to render at the bottom of the layout.
   */
  taskBar?: ReactNode;
  /**
   * Optional trailing content rendered after the main layout container.
   */
  trailing?: ReactNode;
  /**
   * Whether the layout is in a loading state.
   */
  loading?: boolean;
  /**
   * When true, drops the right-hand edge padding for a full-bleed content area.
   */
  fullBleed?: boolean;
};

/**
 * AppLayout component applies the layout structure of the application.
 * It contains the navigation, header, taskbar and the main content.
 */
export const AppLayout: FC<AppLayoutProps> = ({
  leading,
  navigation,
  taskBar = <TaskBar />,
  trailing,
  loading = false,
  fullBleed = false,
  children,
}) => {
  const { isMenuOpen: open } = useNavigationState();

  return (
    <Flex
      className={layoutContainerStyle({
        open,
        hasTrailing: !!trailing,
        fullBleed,
      })}
    >
      {navigation}
      <div className={baseContainerStyle()}>
        <div className={layoutMaskStyle} />
        {leading}
        <div className={contentContainerStyle}>
          {loading && (
            <ProgressBar
              variant="infinite"
              position="absolute"
              className={progressBarStyle}
            />
          )}
          {children}
        </div>
        {taskBar}
      </div>
      {trailing}
    </Flex>
  );
};
